import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { getGetProfitLossQueryKey, useGetProfitLoss, useRebuildProfitLossMonth, type ProfitLossMonth } from "@workspace/api-client-react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/hooks/use-auth";

const monthNames = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat("en-GB", { month: "short" }).format(new Date(2024, i, 1)),
);
const pounds = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 });

type Brand = "tss" | "versa";

export function ProfitLossTable({ months, year }: { months: ProfitLossMonth[]; year: number }) {
  const values = new Map(months.map((month) => [month.month, month]));
  const rows: { key: Brand; title: string; prefix: string }[] = [
    { key: "tss", title: "TSS", prefix: "TSS" },
    { key: "versa", title: "VERSA", prefix: "VER" },
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[1320px] border-collapse text-left text-sm">
        <caption className="sr-only">Monthly share of invoice sales for TSS and VERSA in {year}</caption>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th scope="col" className="sticky left-0 z-10 min-w-36 border-r border-slate-200 bg-slate-50 px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Range</th>
            {monthNames.map((name) => (
              <th key={name} scope="col" className="min-w-28 px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-slate-100 last:border-0 hover:bg-orange-50/40">
              <th scope="row" className="sticky left-0 z-10 border-r border-slate-200 bg-white px-5 py-5 font-semibold text-slate-900">
                <span className="block">{row.title}</span>
                <span className="mt-1 block text-xs font-normal text-slate-500">{row.prefix}* parts</span>
              </th>
              {monthNames.map((name, index) => {
                const month = values.get(index + 1);
                const percent = row.key === "tss" ? month?.tssPercentage : month?.versaPercentage;
                const value = row.key === "tss" ? month?.tssValue : month?.versaValue;
                return (
                  <td key={name} className="px-2 py-5 text-center tabular-nums" title={month?.invoiceCount
                    ? `${row.title} parts: ${pounds.format(value ?? 0)} / webref3 invoice total: ${pounds.format(month.denominator)} (${month.invoiceCount} invoices)`
                    : `No qualifying webref3 invoices in ${name} ${year}`}>
                    {percent == null ? (
                      <span className="text-slate-400" aria-label={`No qualifying invoices in ${name} ${year}`}>—</span>
                    ) : (
                      <>
                        <span className="block font-semibold text-slate-900">{percent.toFixed(1)}%</span>
                        <span className="mt-1 block text-xs text-slate-500">{pounds.format(value ?? 0)}</span>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProfitLossPage() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const queryClient = useQueryClient();
  const { isRealAdmin, viewAsUser } = useAuth();
  const rebuild = useRebuildProfitLossMonth({
    mutation: {
      onSuccess: (report) => queryClient.setQueryData(getGetProfitLossQueryKey({ year: report.year }), report),
    },
  });
  const { data, isLoading, isFetching, isError, refetch } = useGetProfitLoss(
    { year },
    { query: { queryKey: getGetProfitLossQueryKey({ year }), staleTime: 5 * 60_000, refetchInterval: 60 * 60_000, retry: false } },
  );
  return (
    <Layout fullWidth>
      <div className="mx-auto max-w-[1720px] space-y-6 px-4 py-7 sm:px-7">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-orange-600">Sales analysis</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">P&amp;L's</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Monthly share of sales on invoices with a populated AINVHEAD.webref3.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous year" disabled={year <= 2000}
              onClick={() => setYear((value) => value - 1)}
              className="rounded-lg border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-20 rounded-lg border border-slate-200 bg-white px-4 py-2 text-center font-semibold tabular-nums text-slate-900" aria-live="polite">{year}</span>
            <button type="button" aria-label="Next year" disabled={year >= 2100}
              onClick={() => setYear((value) => value + 1)}
              className="rounded-lg border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              <ChevronRight size={18} />
            </button>
            <button type="button" aria-label="Refresh report" disabled={isFetching}
              onClick={() => void refetch()}
              className="ml-2 rounded-lg border border-slate-200 bg-white p-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
              <RefreshCw size={18} className={isFetching ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {isRealAdmin && !viewAsUser && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <label htmlFor="profit-loss-month">Rebuild month from Merlin:</label>
            <select id="profit-loss-month" value={month} onChange={(event) => setMonth(Number(event.target.value))}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              {monthNames.map((name, index) => <option value={index + 1} key={name}>{name}</option>)}
            </select>
            <button type="button" disabled={rebuild.isPending || year > new Date().getFullYear()}
              onClick={() => { if (window.confirm(`Rebuild ${monthNames[month - 1]} ${year} from Merlin?`)) rebuild.mutate({ data: { year, month } }); }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 font-medium hover:bg-slate-50 disabled:opacity-40">
              {rebuild.isPending ? "Rebuilding…" : "Rebuild saved month"}
            </button>
            {rebuild.isError && <span role="alert" className="text-red-700">Rebuild failed. Saved figures are unchanged.</span>}
          </div>
        )}

        <div className="rounded-xl border border-orange-100 bg-orange-50/60 px-5 py-4 text-sm leading-relaxed text-slate-700">
          <strong className="text-slate-900">How it is calculated:</strong> Each percentage divides the net value
          of TSS* or VER* parts on invoices with a non-blank AINVHEAD.webref3 by the full net value of
          all those invoices. Other parts count only in the denominator; product group 0999 is no longer required.
          Invoice month is used; VAT and credit notes are excluded. Hover over a cell for the invoice count and denominator.
        </div>

        {isLoading ? (
          <div role="status" className="rounded-xl border border-slate-200 bg-white px-5 py-16 text-center text-slate-500">Loading saved invoice figures…</div>
        ) : isError ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-5 py-8 text-center text-red-800">
            Could not load the invoice report. No figures are shown.
            <button type="button" onClick={() => void refetch()} className="ml-3 font-semibold underline">Try again</button>
          </div>
        ) : data ? (
          <>
            <ProfitLossTable months={data.months} year={year} />
            <p className="text-xs text-slate-500">
              Sales excluding VAT · webref3 invoices counted once each · a dash means there were no qualifying invoices.
              {" Last updated: "}{new Date(data.generatedAt).toLocaleString("en-GB", { timeZone: "Europe/London", dateStyle: "medium", timeStyle: "short" })}.
              {isFetching && " Refreshing…"}
            </p>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
